import {
  DatePicker,
  DatePickerProps,
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';

import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/util';
import { useState } from 'react';
import { MOUSE_EVENT_BUTTONS } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/const';
import { createTemplatePlaceholder } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/util';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './TemplatePlaceholderContextMenu';
import { PLACEHOLDER_BLUE } from './colours';

export const OPEN_PICKER_BUTTON_SLOTS = {
  openPickerButton: {
    sx: { visibility: 'hidden' },
  },
};

export const PLACEHOLDER_SLOT_PROPS = {
  // Intercept the click before it reaches the segments
  onMouseDown: (e: { button: number; preventDefault: () => void }) => {
    // Only prevent default if it's a left click (0)
    // This keeps Right Click (2) working for the context menu
    if (e.button === 0) {
      e.preventDefault();
    }
  },

  slotProps: {
    input: {
      readOnly: true,
      tabIndex: -1, // Stops keyboard selection
    },
  },
};

export type TemplateCapableDatePickerProps = Omit<DatePickerProps, 'value' | 'onChange'> & {
  value: DatePickerProps['value'] | TemplatePlaceholder;
  onChange: (
    newValue: DatePickerProps['value'] | TemplatePlaceholder,
    reason: 'normal' | 'setIsTemplatePlaceholder' | 'unsetIsTemplatePlaceholder',
    /** MUI's validation result for the new value; only provided for 'normal' changes */
    validationError?: DateValidationError | null
  ) => void;
  isError?: boolean;
  isOnTemplatePage?: boolean;

  templateBuilderDisplayName?: string;
};

export default function TemplateCapableDatePicker({
  value,
  onChange,
  isError = false,
  templateBuilderDisplayName = '',
  isOnTemplatePage = false,
  disabled = false,
  sx,
  slotProps: callerSlotProps,
  ...props
}: TemplateCapableDatePickerProps) {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const valueIsPlaceholder = isTemplatePlaceholder(value);

  // Caller text field props (e.g. fullWidth, onBlur) are merged over our own so
  // they don't discard the placeholder/context-menu handling below.
  const callerTextFieldSlotProps =
    typeof callerSlotProps?.textField === 'object' ? callerSlotProps.textField : undefined;

  const placeholderTextFieldSlotProps = (
    valueIsPlaceholder
      ? {
          color: 'info' as const,
          sx: {
            ...sx,
            cursor: 'default',
            '& .MuiFilledInput-root.Mui-focused': {
              backgroundColor: PLACEHOLDER_BLUE,
              color: 'transparent !important',
              pointerEvents: 'none',
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              backgroundColor: PLACEHOLDER_BLUE,
              color: 'transparent !important',
              pointerEvents: 'none',
            },
            '& *::selection': {
              backgroundColor: 'transparent !important',
            },
            '& input::placeholder': {
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
          focused: true,
          slotProps: {
            htmlInput: { placeholder: DEFAULT_PLACEHOLDER_FIELD_LABEL },
          },
        }
      : {
          sx,
        }
  ) as NonNullable<NonNullable<DatePickerProps['slotProps']>['textField']>;

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      onChange(createTemplatePlaceholder(templateBuilderDisplayName), 'setIsTemplatePlaceholder');
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      onChange(null, 'unsetIsTemplatePlaceholder');
    }
  };

  if (isOnTemplatePage) {
    return (
      <>
        <DatePicker
          value={valueIsPlaceholder ? null : value}
          format="dd/MM/yyyy"
          onChange={(
            value: PickerValue,
            context: PickerChangeHandlerContext<DateValidationError>
          ) => onChange(value, 'normal', context.validationError)}
          slotProps={{
            ...(valueIsPlaceholder ? OPEN_PICKER_BUTTON_SLOTS : {}),
            ...callerSlotProps,
            textField: {
              ...(valueIsPlaceholder ? PLACEHOLDER_SLOT_PROPS : {}),

              onMouseDownCapture: (evt: React.MouseEvent) => {
                if (isOnTemplatePage && evt.button === MOUSE_EVENT_BUTTONS.right) {
                  evt.preventDefault();
                  evt.stopPropagation();
                }
              },
              onContextMenuCapture: (evt: React.MouseEvent) => {
                if (isOnTemplatePage) {
                  evt.preventDefault();
                  evt.stopPropagation();
                }
                if (isOnTemplatePage && !disabled) {
                  handleOpenPlaceholderContextMenu(
                    evt,
                    placeholderMenuState,
                    setPlaceholderMenuState
                  );
                }
              },
              error: isError,
              ...placeholderTextFieldSlotProps,
              ...callerTextFieldSlotProps,
            },
          }}
          readOnly={valueIsPlaceholder}
          disabled={disabled}
          {...props}
        />
        <TemplatePlaceholderContextMenu
          menuState={placeholderMenuState}
          placeholderActive={valueIsPlaceholder}
          onClose={handleCloseTemplatePlaceholderContextMenu}
        />
      </>
    );
  }

  if (valueIsPlaceholder) {
    throw new Error(
      'TemplateCapableDatePicker: value is a placeholder but isOnTemplatePage is false'
    );
  }

  return (
    <DatePicker
      value={value}
      format="dd/MM/yyyy"
      onChange={(value: PickerValue, context: PickerChangeHandlerContext<DateValidationError>) =>
        onChange(value, 'normal', context.validationError)
      }
      disabled={disabled}
      slotProps={callerSlotProps}
      {...props}
    />
  );
}
